<script setup lang="ts">
/**
 * BlogSearchBox.vue
 * Nuxt UI v4 blog search box component.
 * Story: 115-122-T002
 *
 * Provides a search input + submit/clear buttons with WCAG 2.1 AA accessibility:
 *   - aria-label on UInput (L146 pattern)
 *   - role="status" + aria-live="polite" on result count region
 *   - No-results state with recovery actions
 *
 * NOTE: Does NOT use useRoute() — URL state is managed by T003 (blog/index.vue).
 */

import { computed, ref, onMounted, useTemplateRef, watch } from 'vue'

const showFilters = ref(false)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface Props {
  /** v-model for search query (optional — composable may manage internally) */
  modelValue?: string
  /** Number of results found */
  resultCount?: number
  /** Whether a search is currently active */
  hasSearch?: boolean
  /** True while async search is in progress */
  isSearching?: boolean
  /** Current search query text (used for display and clear button visibility) */
  searchQuery?: string
  /** Active product filter */
  selectedProduct?: string | null
  /** Active category filter */
  selectedCategory?: string | null
  /** Available product filter options */
  productOptions?: string[]
  /** Available category filter options */
  categoryOptions?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  resultCount: 0,
  hasSearch: false,
  isSearching: false,
  searchQuery: '',
  selectedProduct: null,
  selectedCategory: null,
  productOptions: () => [],
  categoryOptions: () => [],
})

// ---------------------------------------------------------------------------
// Emits
// ---------------------------------------------------------------------------
const emit = defineEmits<{
  search: [query: string]
  clear: []
  filterChange: [payload: { product: string | null; category: string | null }]
  clearFilters: []
}>()

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------
/** Tracks the input value locally; initialised from `searchQuery` prop on mount */
const localQuery = ref<string>('')
const ALL_FILTER_VALUE = '__all__'

const localProduct = ref<string>(ALL_FILTER_VALUE)
const localCategory = ref<string>(ALL_FILTER_VALUE)

function toSelectValue(value: string | null | undefined): string {
  return value ?? ALL_FILTER_VALUE
}

function fromSelectValue(value: string): string | null {
  return value === ALL_FILTER_VALUE || value.length === 0 ? null : value
}

const productSelectItems = computed(() => ([
  { label: 'All products', value: ALL_FILTER_VALUE },
  ...props.productOptions.map(option => ({ label: option, value: option })),
]))

const categorySelectItems = computed(() => ([
  { label: 'All categories', value: ALL_FILTER_VALUE },
  ...props.categoryOptions.map(option => ({ label: option, value: option })),
]))

const statusMessage = computed(() => {
  if (props.isSearching) {
    return 'Searching...'
  }

  if (props.hasSearch) {
    const noun = props.resultCount === 1 ? 'article' : 'articles'
    return `${props.resultCount} ${noun} found for "${props.searchQuery}"`
  }

  return 'Search blog articles'
})

/** Template ref to the UInput element for focus management */
const searchInputRef = useTemplateRef('searchInput')

onMounted(() => {
  if (props.searchQuery) {
    localQuery.value = props.searchQuery
  }
  localProduct.value = toSelectValue(props.selectedProduct)
  localCategory.value = toSelectValue(props.selectedCategory)
})

watch(() => props.selectedProduct, (next) => {
  localProduct.value = toSelectValue(next)
})

watch(() => props.selectedCategory, (next) => {
  localCategory.value = toSelectValue(next)
})

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------
function handleSubmit(): void {
  const trimmed = localQuery.value.trim()
  if (trimmed.length > 0) {
    emit('search', trimmed)
  }
}

function handleTryDifferent(): void {
  localQuery.value = ''
  // Focus the input element so the user can type a new query immediately
  const host = (searchInputRef.value as { $el?: unknown } | null)?.$el
  if (!host || typeof host !== 'object') return

  const inputElement =
    'tagName' in host && (host as { tagName?: string }).tagName === 'INPUT'
      ? host
      : ('querySelector' in host && typeof (host as { querySelector?: unknown }).querySelector === 'function'
          // eslint-disable-next-line no-unused-vars
          ? (host as { querySelector: (selector: string) => unknown }).querySelector('input')
          : null)

  if (
    inputElement &&
    typeof inputElement === 'object' &&
    'focus' in inputElement &&
    typeof (inputElement as { focus?: unknown }).focus === 'function'
  ) {
    ;(inputElement as { focus: () => void }).focus()
  }
}

function handleClear(): void {
  localQuery.value = ''
  emit('clear')
}

function handleFilterChange(): void {
  emit('filterChange', {
    product: fromSelectValue(localProduct.value),
    category: fromSelectValue(localCategory.value),
  })
}

function handleClearFilters(): void {
  localProduct.value = ALL_FILTER_VALUE
  localCategory.value = ALL_FILTER_VALUE
  emit('clearFilters')
}
</script>

<template>
  <div class="blog-search-box w-full">
    <!-- Compact inline search form (OD nav-search pattern) -->
    <form
      class="flex w-full items-center gap-2"
      @submit.prevent="handleSubmit"
    >
      <!-- Search input with inline icon -->
      <div class="relative flex-1">
        <svg
          class="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
          />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <UInput
          ref="searchInput"
          v-model="localQuery"
          placeholder="Search articles..."
          aria-label="Search blog articles"
          class="w-full pl-8"
          :ui="{ base: 'h-9 rounded-full border-gray-300 text-sm pl-9' }"
          @keyup.enter="handleSubmit"
        />
        <!-- Inline filter toggle button inside the search input (OD pattern) -->
        <UButton
          type="button"
          color="neutral"
          variant="ghost"
          class="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          :class="(selectedProduct || selectedCategory) ? 'bg-spruce-50 text-spruce-800' : ''"
          aria-label="Toggle filters"
          @click="showFilters = !showFilters"
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M22 3H2l8 9v6l4 3v-9l8-9z" />
          </svg>
        </UButton>
      </div>

      <UButton
        type="submit"
        size="sm"
        class="shrink-0 rounded-full px-4 text-sm font-semibold"
        @click="handleSubmit"
      >
        Search
      </UButton>

      <UButton
        v-if="searchQuery && searchQuery.length > 0"
        variant="ghost"
        size="sm"
        aria-label="Clear search"
        class="shrink-0 rounded-full px-2 text-gray-400"
        @click="handleClear"
      >
        ×
      </UButton>
    </form>

    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      class="sr-only"
    >
      {{ statusMessage }}
    </div>

    <!-- Filter popover (shown below the bar, hidden by default) -->
    <div
      v-if="showFilters"
      class="mt-2 grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-md sm:grid-cols-2"
    >
      <label class="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Product
        <USelect
          v-model="localProduct"
          :items="productSelectItems"
          aria-label="Filter blog by product"
          size="sm"
          @change="handleFilterChange"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs font-medium text-gray-600">
        Category
        <USelect
          v-model="localCategory"
          :items="categorySelectItems"
          aria-label="Filter blog by category"
          size="sm"
          @change="handleFilterChange"
        />
      </label>
      <div class="sm:col-span-2 flex justify-end">
        <UButton
          size="sm"
          variant="ghost"
          class="text-xs"
          aria-label="Clear blog filters"
          @click="handleClearFilters"
        >
          Clear Filters
        </UButton>
      </div>
    </div>

    <!-- No-results state -->
    <div
      v-if="hasSearch && resultCount === 0 && !isSearching"
      class="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center"
    >
      <p class="mb-3 text-gray-700">
        No articles found for '<strong>{{ searchQuery }}</strong>'
      </p>

      <div class="flex flex-col gap-2 sm:flex-row sm:justify-center">
        <UButton
          variant="outline"
          @click="handleTryDifferent"
        >
          Try a different search
        </UButton>

        <UButton
          variant="outline"
          @click="handleClear"
        >
          Clear Search
        </UButton>

        <NuxtLink to="/blog">
          <UButton variant="solid">
            Browse All Articles
          </UButton>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
